import toast from "react-hot-toast";

// A server reason longer than this is dropped in favour of the fallback, so the cap is
// the point at which the user stops being told anything. It exists to keep a stack
// trace or a raw transport string out of a toast, not to withhold a sentence a
// clinician can act on — and at 170 it was doing the second: the GMS-number reason on a
// vaccine cancellation is 243 characters, so the only thing shown was "Oops, something
// went wrong!" while the response carried the answer (KIN-1738).
const MAX_MESSAGE_LENGTH = 300;

/**
 * What to tell the user about a failed request.
 *
 * The backend's own reason first, then whatever the caller offered, then a generic
 * line. Shared rather than duplicated: this app had two helpers deciding this
 * independently and they disagreed — one read `response.data.message` and one never
 * did, so the same failure was explained or not depending on which hook happened to
 * catch it (KIN-1725).
 *
 * `error.message` is deliberately below the caller's fallback in that order. It holds
 * axios transport text — "Request failed with status code 400" — which is worse than a
 * caller who has said what was being attempted.
 */
/**
 * The per-field reasons behind a validation failure, joined into one sentence.
 *
 * `ApiExceptionHandler` answers a rejected `@Valid` with `ApiResponse.error`, whose
 * top-level `message` is the constant "Validation failed" and whose `errors` array
 * holds what actually went wrong, one entry per field. Reading only the top level
 * therefore tells a clinician nothing, which is what "Validation failed" and Spring's
 * own "Error count: 1" both amounted to.
 *
 * Field names are dropped rather than shown: they are Java property names, and a
 * clinician does not know which control `administrationNotesCode` is. The messages read
 * as sentences on their own.
 */
const getFieldReasons = (error) => {
  const errors = error?.response?.data?.errors;

  if (!Array.isArray(errors)) return "";

  return errors
    .map((entry) => entry?.message)
    .filter((message) => typeof message === "string" && message.trim())
    .join(" ");
};

const isUsable = (message) =>
  typeof message === "string" &&
  !!message.trim() &&
  message.length <= MAX_MESSAGE_LENGTH;

export const getServerErrorMessage = (
  error,
  fallback = "Oops, something went wrong!"
) => {
  const serverMessage = error?.response?.data?.message;
  const fieldReasons = getFieldReasons(error);

  // The field reasons first when there are any: the envelope's own message is a
  // category ("Validation failed") and they are the substance.
  if (isUsable(fieldReasons)) {
    return fieldReasons;
  }

  if (isUsable(serverMessage)) {
    return serverMessage;
  }

  // A long list of field reasons still beats a generic line — the first one is enough
  // to act on, and saying nothing is the failure mode this helper exists to prevent.
  if (fieldReasons.trim()) {
    return fieldReasons.slice(0, MAX_MESSAGE_LENGTH).trimEnd();
  }

  return fallback || error?.message || "Oops, something went wrong!";
};

export const showMutationError = (
  error,
  fallback = "Oops, something went wrong!"
) => {
  console.error(error);

  toast.error(getServerErrorMessage(error, fallback));
};
